import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { toRFC3339 } from './datetime'
import { listQuery } from './query'

const api = clientFor('/scr/api')

/** 响应状态机：pending → done / cancelled */
export type ResponseStatus = 'pending' | 'done' | 'cancelled'

/** 响应动作类型：与后端 internal/response/service.go 的 Actions 同源两份 */
export type ResponseActionType = 'restock' | 'expedite' | 'transfer' | 'cancel' | 'notify' | 'other'

/** 响应级动作，与后端 POST /responses/:id/done|cancel 一一对应 */
export type ResponseAction = 'done' | 'cancel'

/** 关联对象：alert_id 与 demand_id 二选一（spec §5.1） */
export type ResponseLink = 'alert' | 'demand'

/**
 * 一条响应。字段与 services/scr/internal/response/model.go 的 Response 一一对应。
 * 刻意不叫 `Response`：那是 DOM 的全局类型，同名会让读代码的人怀疑是不是 fetch 的 Response。
 */
export interface ResponseRow {
  id: number
  tenant_id: number
  /** 响应单号，前缀 XZ */
  doc_no: string
  /** 与预警关联，与 demand_id 二选一；后端 tag 带 omitempty，未关联时字段不出现 */
  alert_id?: number | null
  /** 与需求关联，与 alert_id 二选一 */
  demand_id?: number | null
  action: ResponseActionType
  detail: string
  owner: string
  /** 预计完成时间，后端建单必填 */
  due_at: string
  status: ResponseStatus
  done_at?: string | null
  created_at: string
  updated_at: string
}

/** 详情与列表行同形（ResponseDetail 只是内嵌） */
export type ResponseDetail = ResponseRow

/** 建单入参：单号与 status 由服务端处理，都不在入参里；alert_id / demand_id 必须且只能送一个 */
export interface ResponseInput {
  action: ResponseActionType
  owner: string
  due_at: string
  detail?: string
  alert_id?: number
  demand_id?: number
}

/** 完成 / 取消入参：后端把它追加到 detail 末尾（"[完成] ..." / "[取消] ..."），可留空 */
export interface ResponseActionInput {
  detail?: string
}

export interface ResponseQuery extends PageQuery {
  keyword?: string
  status?: string
  action?: string
  /** 关联对象筛选：后端 Query 里有 AlertID / DemandID 两个指针字段 */
  alert_id?: number
  demand_id?: number
}

/** 表单态：没选对象时两个 id 都是 undefined，提交时只送被选中的那个 */
export interface ResponseForm {
  link: ResponseLink
  alert_id?: number
  demand_id?: number
  action: ResponseActionType | ''
  owner: string
  due_at: string | null
  detail: string
}

/** 动作字典，顺序与后端 response.Actions 一致，即前端下拉的展示顺序 */
export const ACTION_TYPES: ResponseActionType[] = [
  'restock',
  'expedite',
  'transfer',
  'cancel',
  'notify',
  'other',
]

/** 与后端 services/scr/internal/response/service.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<ResponseStatus, string> = {
  pending: '待执行',
  done: '已完成',
  cancelled: '已取消',
}

/** 动作类型没有后端文案字典（validateInput 里只是一张白名单 map），文案归前端 */
export const ACTION_LABELS: Record<ResponseActionType, string> = {
  restock: '补货',
  expedite: '加急',
  transfer: '调拨',
  cancel: '取消',
  notify: '通知',
  other: '其他',
}

/** 状态 → 卡片标签色 */
export const STATUS_TAG: Record<ResponseStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    pending: 'warning',
    done: 'success',
    cancelled: 'info',
  }

/** 看板三列的固定顺序，与后端 StatusNames 的枚举顺序一致 */
export const BOARD_COLUMNS: ResponseStatus[] = ['pending', 'done', 'cancelled']

/**
 * 各状态允许的动作，抄后端 internal/response/service.go：
 * Done 与 Cancel 都先挡「不是 pending」，所以 done / cancelled 之后都是只读。
 */
const ACTIONS: Record<ResponseStatus, ResponseAction[]> = {
  pending: ['done', 'cancel'],
  done: [],
  cancelled: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: ResponseStatus, action: ResponseAction): boolean {
  return ACTIONS[status].includes(action)
}

/**
 * 表单 → 请求体：只送被选中的那个关联 id。
 * 后端 validateInput 是异或判断（hasAlert == hasDemand 即 422），两个都送或都不送都会被挡，
 * 所以这里不做任何"两边都填上"的兜底。
 */
export function responseInput(form: ResponseForm): ResponseInput {
  const input: ResponseInput = {
    action: (form.action || 'other') as ResponseActionType,
    owner: form.owner.trim(),
    // due_at 后端必填（零值 422），表单校验已保证选了时间
    due_at: toRFC3339(form.due_at) ?? '',
  }
  if (form.link === 'alert') {
    if (form.alert_id) input.alert_id = form.alert_id
  } else if (form.demand_id) {
    input.demand_id = form.demand_id
  }
  const detail = form.detail.trim()
  if (detail) input.detail = detail
  return input
}

export const listResponses = (q: ResponseQuery): Promise<PageResult<ResponseRow>> =>
  api.get<PageResult<ResponseRow>>('/responses', listQuery({ ...q }))

export const getResponse = (id: number): Promise<ResponseDetail> =>
  api.get<ResponseDetail>(`/responses/${id}`)

export const createResponse = (input: ResponseInput): Promise<ResponseRow> =>
  api.post<ResponseRow>('/responses', input)

/** 完成：pending → done。该预警下没有 pending 响应时后端把预警自动置 resolved */
export const doneResponse = (id: number, input: ResponseActionInput = {}): Promise<ResponseRow> =>
  api.post<ResponseRow>(`/responses/${id}/done`, { ...input })

/** 取消：pending → cancelled。不像 done 那样触发预警自动解决 */
export const cancelResponse = (id: number, input: ResponseActionInput = {}): Promise<ResponseRow> =>
  api.post<ResponseRow>(`/responses/${id}/cancel`, { ...input })
